import React, { useState, useRef } from 'react'
import styled from 'styled-components'
import useClickOutSide from 'hooks/use-click-outside-hooks'
import BellIcon from 'assets/illustration-bell.svg'

import { AlertButton, Box, NotificationPanel, Text, VirtualizedList } from 'components'
import { readAllNotifications, getUnReadNotificationCount, updateNotificationStatus } from 'services/notification'
import { Notification as NotificationStore, useAppDispatch } from 'store'
import { Notification } from 'types/global'

import NotificationItem from './NotificationItem'
import { VirtualizedListScrollEvent } from 'components/VirtualizedList'
import { useGetNotifications } from 'queries/notifications'

const PAGE_SIZE = 20

type DaybreakProps = {
  children: string
}

const Daybreak: React.FC<DaybreakProps> = ({ children }) => (
  <DaybreakWrapper>
    <Text color="gray5" fontSize={13} lineHeight="16px" textTransform="uppercase">
      {children}
    </Text>
  </DaybreakWrapper>
)

const EndofList: React.FC = () => (
  <FooterWrapper>
    <BellIcon />
    <Text paddingTop="10px" paddingLeft="20px" paddingRight="20px" textAlign="center">
      notifications_end_of_list
    </Text>
  </FooterWrapper>
)

const Notifications: React.FC = () => {
  const [showNotipanel, setShowNotipanel] = useState(false)
  const [allReadLoading, setAllReadLoading] = useState(false)
  const notiwrapperRef = useRef(null)
  const dispatch = useAppDispatch()

  const handleClosePanel = () => {
    setShowNotipanel(false)
  }

  useClickOutSide(notiwrapperRef, handleClosePanel)

  const {
    list: notifications,
    totalCount: notiCount,
    isFetchingNextPage,
    isLoading: loading,
    fetchNextPage,
    refetch,
  } = useGetNotifications(
    {
      page_size: PAGE_SIZE,
    },
    {
      getNextPageParam: (lastPage) => lastPage.data[lastPage.data.length - 1]?.recordId,
      enabled: true,
    },
  )

  const isLoading = isFetchingNextPage || loading

  const rowGetter = (index: number) => {
    const data = notifications[index]

    if (data.itemType === 'daybreakolder') {
      return <Daybreak>Older</Daybreak>
    }
    if (data.itemType === 'daybreaktoday') {
      return <Daybreak>Today</Daybreak>
    }

    if (data.itemType === 'endofnotification') {
      return <EndofList />
    }

    const tempData = data as Notification

    return (
      <>
        <NotificationItem
          {...tempData}
          index={index}
          onReadStatusClick={(_: number, isRead: boolean) => {
            dispatch(
              updateNotificationStatus({
                notification_id: tempData.notificationId,
                seen: !isRead,
                onNotiStatUpdateCallBack: () => {
                  dispatch(getUnReadNotificationCount())
                  dispatch(NotificationStore.addEndofList())
                },
              }),
            )
            refetch()
          }}
        />
      </>
    )
  }

  const handleOnScroll: VirtualizedListScrollEvent = (_e, isBottomReached) => {
    const notificationItems = notifications.filter((i) => i.itemType === 'notification') as Notification[]
    const notificationItemsCount = notificationItems.length

    if (notificationItemsCount > 0 && isBottomReached) {
      const isLastPage = notificationItems[notificationItemsCount - 1].recordId - PAGE_SIZE <= 0

      if (!isLastPage) fetchNextPage()
    }
  }

  return (
    <>
      <Box pr="1rem">
        <AlertButton icon="bell" badgeCount={notiCount} onClick={() => setShowNotipanel(true)} />
      </Box>
      {showNotipanel && (
        <NotificationPanel
          ref={notiwrapperRef}
          title="notification_lbl"
          isEmpty={notifications.length === 0}
          isLoading={isLoading}
          allReadLoading={allReadLoading}
          onMarkAllReadClick={() => {
            setAllReadLoading(true)
            dispatch(
              readAllNotifications(() => {
                setAllReadLoading(false)
                dispatch(getUnReadNotificationCount())
              }),
            )
            refetch()
          }}
          onPanelClose={handleClosePanel}
          unReadCount={notiCount}
        >
          <Container>
            <VirtualizedList rowCount={notifications.length} rowGetter={rowGetter} onScroll={handleOnScroll} />
          </Container>
        </NotificationPanel>
      )}
    </>
  )
}
const Container = styled.div`
  height: 100%;
`

const DaybreakWrapper = styled.div`
  background-color: ${(p) => p.theme.colors.gray1};
  padding: 9px 0 9px 24px;
`

const FooterWrapper = styled.div`
  position: relative;
  padding-bottom: 20px;
  padding-top: 20px;
  top: 10%;
  display: flex;
  flex-direction: column;
  align-items: center;
`

export default Notifications
